import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  signal,
  inject,
  input,
} from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { TagsAnalytics, TagsTarget } from '@core/analytics-legacy';
import { AnalyticsMode } from '@core/analytics-types';
import { filterBoolean } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { TagsActions } from '@core/tags-and-sources-data';
import { HeadingComponent, IconComponent } from '@core/user-interface-legacy';
import type { Editable } from '@core/utils-legacy';

import { CandidateTagsTypeaheadWrapperComponent } from '../../wrappers/candidate-tags-typeahead-wrapper/candidate-tags-typeahead-wrapper.component';
import { candidateIdSelector, editableTagsSelector } from '../selectors';

@Component({
  selector: 'rt-candidate-tags-container',
  templateUrl: './candidate-tags-container.component.html',
  styleUrls: ['./candidate-tags-container.component.less'],
  providers: [TagsAnalytics],
  imports: [CandidateTagsTypeaheadWrapperComponent, IconComponent, HeadingComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateTagsContainerComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly analytics = inject(TagsAnalytics);

  public readonly tagsVisible = signal(true);

  public readonly withHeading = input<boolean>(true);
  public readonly withEmptyState = input<boolean>(false);

  public tags: Editable<Array<{ value: string; processing: boolean }>>;

  private candidateId$ = this.store.select(candidateIdSelector);
  private ngOnDestroy$ = new Subject<void>();
  private tags$: Observable<Editable<Array<{ value: any; processing: boolean }>>> =
    this.store.select(editableTagsSelector);

  public ngOnInit(): void {
    combineLatest([
      this.store.select(AbilitiesSelectors.hasOne(Ability.ManageCandidates)),
      this.tags$,
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([canManageCandidates, tags]) => {
        this.tags = tags;
        this.cdRef.detectChanges();

        this.tagsVisible.set(!!(canManageCandidates || this.tags.value?.length));
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public saveTag(tagName: string): void {
    this.updateTags(tagName);

    this.candidateId$.pipe(filterBoolean, take(1)).subscribe((candidateId: number) => {
      this.store.dispatch(TagsActions.createTag({ payload: { candidateId, tagName } }));
    });
    this.analytics.trackTagAdded(TagsTarget.Candidate, AnalyticsMode.Single);
  }

  public deleteTag(tagName: string): void {
    this.candidateId$.pipe(filterBoolean, take(1)).subscribe((candidateId: number) => {
      this.store.dispatch(TagsActions.deleteCandidateTags({ payload: { candidateId, tagName } }));
    });
  }

  private updateTags(tagName: string): void {
    this.tags = {
      ...this.tags,
      value: [...(this.tags.value || []), { value: tagName, processing: true }],
    };
    this.cdRef.detectChanges();
  }
}
